import {
  type ReactNode,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link, useLocation } from "wouter";
import { ChevronRight, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePublicPageMeta } from "@/hooks/usePublicPageMeta";
import { HELP_ARTICLES } from "@/content/help/articles";
import { searchHelp } from "@/content/help/search";
import { track } from "@/lib/analytics";
import { interpolate } from "@/components/legal/LegalContent";
import { useHelpVars } from "@/components/help/useHelpVars";

/**
 * Local copy of the search-page highlighter — keeps the dropdown
 * matches visually consistent with /help/search results. Uses the
 * deterministic split + non-global match regex pattern (the prior
 * `re.test(part)` implementation skipped alternating hits because
 * the global regex shared `lastIndex` across calls).
 */
function escapeRegexLocal(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightMatches(text: string, query: string): ReactNode {
  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
  if (tokens.length === 0) return text;
  const splitRe = new RegExp(`(${tokens.map(escapeRegexLocal).join("|")})`, "gi");
  const matchRe = new RegExp(`^(?:${tokens.map(escapeRegexLocal).join("|")})$`, "i");
  return text.split(splitRe).map((part, i) =>
    matchRe.test(part) ? (
      <mark key={i} className="rounded bg-yellow-200/70 px-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export interface HelpBreadcrumb {
  label: string;
  href?: string;
}

interface HelpLayoutProps {
  /** SEO title without the brand suffix; brand is appended automatically. */
  title: string;
  description: string;
  canonical: string;
  breadcrumbs?: HelpBreadcrumb[];
  children: ReactNode;
  /** Hide the hero search bar (e.g. on the dedicated /help/search page). */
  hideHeroSearch?: boolean;
  /** Optional JSON-LD structured-data blob. */
  jsonLd?: Record<string, unknown>;
  /**
   * Emit `noindex, follow` for this page. Use on search-result states where
   * the page itself should not be indexed but links to real articles should
   * remain crawlable. See `PageMeta.noIndexFollow` in lib/seo.ts.
   */
  noIndexFollow?: boolean;
  /**
   * Optional decorative hero background image (landing page only). When set,
   * the hero renders the photo behind a brand-green wash with white text;
   * sub-pages omit it and keep the clean gradient reading hero.
   */
  heroImage?: { webp: string; fallback: string };
}

/**
 * Shared chrome for every Help Centre page: breadcrumb back-link, hero
 * banner with a search bar, and SEO metadata via the app-wide
 * `applyPageMeta` helper. The search input drives the inline results
 * page (/help/search?q=) and pre-fills from the current ?q= so the input
 * stays sticky after navigation.
 */
export function HelpLayout({
  title,
  description,
  canonical,
  breadcrumbs,
  children,
  hideHeroSearch,
  jsonLd,
  noIndexFollow,
  heroImage,
}: HelpLayoutProps) {
  const { t } = useTranslation();
  const vars = useHelpVars();
  // Defense-in-depth: interpolate brand placeholders here too so any caller
  // that forgets to do so on its own can never leak `{{brand}}` into the DOM
  // (task #1514). Pages that already interpolate pass through unchanged.
  const interpolatedTitle = useMemo(() => interpolate(title, vars), [title, vars]);
  const interpolatedDescription = useMemo(
    () => interpolate(description, vars),
    [description, vars],
  );
  const interpolatedBreadcrumbs = useMemo(
    () =>
      breadcrumbs?.map((b) => ({ ...b, label: interpolate(b.label, vars) })),
    [breadcrumbs, vars],
  );
  const [, navigate] = useLocation();
  const initialQ = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("q") || ""
    : "";
  const [query, setQuery] = useState(initialQ);
  // Highlight index for keyboard navigation through the instant-search
  // dropdown. -1 = nothing focused (Enter falls through to a /search nav).
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  usePublicPageMeta(useMemo(() => ({
      title: interpolatedTitle,
      description: interpolatedDescription,
      canonical,
      ogType: "article",
      jsonLd,
      noIndexFollow,
  }), [interpolatedTitle, interpolatedDescription, canonical, jsonLd, noIndexFollow]));

  // Compute instant-search hits as the user types. The corpus is small,
  // so running the scorer on every keystroke is well under a millisecond
  // — no debounce needed. Cap to 6 so the dropdown stays scannable.
  const instantHits = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return [];
    return searchHelp(HELP_ARTICLES, q, 6);
  }, [query]);

  // Reset the keyboard-focus index whenever the hit list shape changes,
  // otherwise the highlight can point past the end of the list.
  useEffect(() => {
    setActiveIdx(-1);
  }, [instantHits.length]);

  // Close the dropdown on outside-click. Pointerdown beats click so we
  // don't fight focus events.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Enter while a result is keyboard-highlighted opens that article;
      // otherwise fall through to the dedicated results page.
      if (activeIdx >= 0 && instantHits[activeIdx]) {
        navigate(`/help/article/${instantHits[activeIdx].article.slug}`);
        setOpen(false);
        return;
      }
      const q = query.trim();
      if (!q) return;
      track("help_search", {
        query: q,
        result_count: instantHits.length,
        surface: "hero",
      });
      navigate(`/help/search?q=${encodeURIComponent(q)}`);
      setOpen(false);
    },
    [navigate, query, activeIdx, instantHits],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!open || instantHits.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % instantHits.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i <= 0 ? instantHits.length - 1 : i - 1));
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [open, instantHits.length],
  );

  return (
    <>
      <section
        className={
          heroImage
            ? "relative bg-brand"
            : "bg-gradient-to-b from-primary/5 via-primary/[0.02] to-transparent"
        }
      >
        {heroImage && (
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <picture>
              <source type="image/webp" srcSet={heroImage.webp} />
              <img
                src={heroImage.fallback}
                alt=""
                className="h-full w-full object-cover object-center"
                loading="eager"
                decoding="async"
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-br from-brand/95 via-brand/85 to-brand/65" />
          </div>
        )}
        <div
          className={
            "relative mx-auto max-w-5xl px-4 sm:px-6 " +
            (heroImage ? "pb-14 pt-12 sm:pb-16 sm:pt-20" : "pb-12 pt-10 sm:pt-14")
          }
        >
          {interpolatedBreadcrumbs && interpolatedBreadcrumbs.length > 0 && (
            <Link href="/help" className="text-sm font-medium text-primary hover:underline">
              {t("helpCentre.back_to_help_centre", { defaultValue: "← Help Centre" })}
            </Link>
          )}
          <h1
            className={
              "mt-3 text-3xl font-bold tracking-tight sm:text-4xl " +
              (heroImage ? "text-white lg:text-5xl" : "text-foreground")
            }
          >
            {interpolatedTitle}
          </h1>
          {interpolatedDescription && (
            <p
              className={
                "mt-3 max-w-3xl text-base sm:text-lg " +
                (heroImage ? "text-white/85" : "text-muted-foreground")
              }
            >
              {interpolatedDescription}
            </p>
          )}

          {!hideHeroSearch && (
            <div ref={containerRef} className="relative mt-6 max-w-2xl">
              <form
                onSubmit={onSubmit}
                role="search"
                className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30"
              >
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  name="q"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  onKeyDown={onKeyDown}
                  placeholder={t("helpCentre.search_placeholder", { defaultValue: "Search help articles" })}
                  aria-label={t("helpCentre.search_placeholder", { defaultValue: "Search help articles" })}
                  aria-autocomplete="list"
                  aria-controls="help-instant-results"
                  aria-expanded={open && instantHits.length > 0}
                  aria-activedescendant={
                    activeIdx >= 0 ? `help-instant-result-${activeIdx}` : undefined
                  }
                  className="h-11 sm:h-9 flex-1 border-0 bg-transparent text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                  data-testid="help-hero-search-input"
                />
                <button
                  type="submit"
                  className="touch-44 cursor-pointer rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  data-testid="help-hero-search-submit"
                >
                  {t("helpCentre.search_button", { defaultValue: "Search" })}
                </button>
              </form>
              {open && instantHits.length > 0 && (
                <ul
                  id="help-instant-results"
                  role="listbox"
                  data-testid="help-instant-results"
                  className="absolute left-0 right-0 z-30 mt-2 max-h-96 overflow-y-auto rounded-2xl border border-border bg-white py-2 shadow-lg"
                >
                  {instantHits.map(({ article }, i) => (
                    <li key={article.slug} role="presentation">
                      <Link
                        href={`/help/article/${article.slug}`}
                        id={`help-instant-result-${i}`}
                        role="option"
                        aria-selected={i === activeIdx}
                        onClick={() => setOpen(false)}
                        className={
                          "block px-4 py-2 text-sm transition-colors " +
                          (i === activeIdx
                            ? "bg-primary/10 text-foreground"
                            : "text-foreground hover:bg-muted/40")
                        }
                        data-testid={`help-instant-result-${article.slug}`}
                      >
                        <div className="font-medium">
                          {highlightMatches(interpolate(article.title, vars), query)}
                        </div>
                        <div className="line-clamp-1 text-xs text-muted-foreground">
                          {highlightMatches(interpolate(article.summary, vars), query)}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {interpolatedBreadcrumbs && interpolatedBreadcrumbs.length > 0 && (
            <nav aria-label={t("helpCentre.breadcrumb_aria", { defaultValue: "Breadcrumb" })} className="mt-6 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              <Link href="/help" className="hover:text-foreground hover:underline">
                {t("helpCentre.title", { defaultValue: "Help Centre" })}
              </Link>
              {interpolatedBreadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1">
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                  {crumb.href ? (
                    <Link href={crumb.href} className="hover:text-foreground hover:underline">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground" aria-current="page">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
        </div>
      </section>

      <main className="mx-auto max-w-5xl px-4 pb-20 sm:px-6">{children}</main>
    </>
  );
}
